import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Timer, Trophy, ArrowRight, PlayCircle, ExternalLink } from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import { getSprintOverview } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

function ModeCard({ item, best }) {
  const isSimulation = item.kind === "simulation";
  const isMarathon = item.kind === "marathon";
  const cta = isSimulation ? "Start simulation" : isMarathon ? "Start marathon" : "Start sprint";

  return (
    <div
      data-testid={STUDY.sprintCard}
      className="flex h-full flex-col rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.05)]"
    >
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--ibp-offwhite)] text-[var(--ibp-navy-950)]">
        <Timer className="h-5 w-5" />
      </span>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-[var(--ibp-ink)]">{item.title}</h3>
        {item.official_format ? (
          <span className="rounded-full bg-[color:rgba(21,128,61,0.10)] px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#15803d]">
            Official length + timing
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">{item.description}</p>
      <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold">
        <span className="rounded-full bg-[var(--ibp-offwhite)] px-2.5 py-1 text-[color:rgba(11,18,32,0.7)]">
          {item.question_count} questions
        </span>
        <span className="rounded-full bg-[var(--ibp-offwhite)] px-2.5 py-1 text-[color:rgba(11,18,32,0.7)]">
          {item.recommended_minutes} min
        </span>
        {item.passing_score ? (
          <span className="rounded-full bg-[var(--ibp-offwhite)] px-2.5 py-1 text-[color:rgba(11,18,32,0.7)]">
            {item.passing_score}% practice target
          </span>
        ) : null}
      </div>
      {best ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[var(--ibp-ink)]">
          <Trophy className="h-3.5 w-3.5 text-[var(--ibp-gold-500)]" />
          Best: {best.percent_score}% ({best.score}/{best.total_count})
        </p>
      ) : null}
      <Link
        to={`/study/mini-exams/${item.sprint_type}`}
        data-testid={`${STUDY.sprintStart}-${item.sprint_type}`}
        className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-4 py-2.5 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] transition-colors hover:bg-[var(--ibp-primary-hover)]"
      >
        {cta} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export default function StudyMiniExams() {
  const { data, isLoading } = useQuery({
    queryKey: ["sprint-overview"],
    queryFn: getSprintOverview,
  });

  const types = (data && data.types) || [];
  const simulations = types.filter((item) => item.kind === "simulation" || item.kind === "marathon");
  const sprints = types.filter((item) => item.kind === "sprint");
  const bestByType = (data && data.best_by_type) || {};
  const recent = (data && data.recent) || [];
  const active = data && data.active;

  return (
    <StudyShell testId={STUDY.miniExamsPage}>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
        Exam-style practice
      </p>
      <h1 className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">Exam Mode</h1>
      <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
        Timed, self-scored simulations using the active question bank. These are study tools,
        not official PSI or IDFPR exams.
      </p>

      {active && (
        <div
          data-testid={STUDY.sprintResumeBanner}
          className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.1)] p-4"
        >
          <p className="text-sm font-semibold text-[var(--ibp-ink)]">
            You have an exam-style attempt in progress. Pick up where you left off.
          </p>
          <Link
            to={`/study/mini-exams/${active.sprint_type}`}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <PlayCircle className="h-4 w-4" /> Resume attempt
          </Link>
        </div>
      )}

      {isLoading ? (
        <p className="mt-8 text-sm text-[color:rgba(11,18,32,0.6)]">Loading...</p>
      ) : (
        <>
          <section className="mt-7">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Exact-length simulations</h2>
                <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.62)]">
                  Match the official National and Illinois question counts and time limits.
                </p>
              </div>
              {simulations.find((item) => item.source_url)?.source_url ? (
                <a
                  href={simulations.find((item) => item.source_url).source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F3D8A] hover:underline"
                >
                  Official PSI format <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : null}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              {simulations.map((item) => (
                <ModeCard key={item.sprint_type} item={item} best={bestByType[item.sprint_type]} />
              ))}
            </div>
            <p className="mt-3 text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
              Question selection follows the published outline as closely as the active bank allows.
              Practice targets are guidance, not a pass prediction.
            </p>
          </section>

          <section className="mt-8">
            <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Timed Review Sprints</h2>
            <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.62)]">
              Shorter sessions for pacing, weak areas, and daily practice.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {sprints.map((item) => (
                <ModeCard key={item.sprint_type} item={item} best={bestByType[item.sprint_type]} />
              ))}
            </div>
          </section>

          <div className="mt-8">
            <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Recent attempts</h2>
            {recent.length === 0 ? (
              <p className="mt-2 text-sm text-[color:rgba(11,18,32,0.6)]">
                No timed attempts yet. Start one above to track your speed and accuracy.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {recent.map((a) => (
                  <li
                    key={a.attempt_id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-white px-4 py-3"
                  >
                    <span className="text-sm font-semibold text-[var(--ibp-ink)]">
                      {a.sprint_title}
                    </span>
                    <span className="flex items-center gap-3 text-sm text-[color:rgba(11,18,32,0.7)]">
                      <span className="font-semibold text-[var(--ibp-ink)]">
                        {a.percent_score}% ({a.score}/{a.total_count})
                      </span>
                      <span className="hidden sm:inline">{fmtDate(a.submitted_at)}</span>
                      <Link
                        to={`/study/mini-exams/results/${a.attempt_id}`}
                        className="font-semibold text-[var(--ibp-navy-950)] underline-offset-2 hover:underline"
                      >
                        Review
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </StudyShell>
  );
}
